/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../redux/reducers';
import { initTabs, closeTab, closeAllTabs, openNewTab, setActiveTab } from '../../../redux/dynamicTabs/actions';
import { Route, withRouter, NavLink, RouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import withAccessProvider, { WithAccessProviderProps } from '../../../hoc/withAccessProvider';
import { staticTabsList } from '../../../settings/staticTabsList';
import { dynamicTabContent } from '../../../settings/dynamicTabSetup';
import { MainTabs as Tabs, MainTabPane as TabPane, Loader } from 'react-inv-forms';
import { withTranslation, WithTranslation } from 'react-i18next';

type MapStateToProps = ReturnType<typeof mapStateToProps>;
type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;

type Props = MapDispatchToProps & MapStateToProps & WithAccessProviderProps & RouteComponentProps & WithTranslation;

/**
 * Komponent odpowiedzialny za mapowanie zakładek statycznych i dynamicznych
 * oraz przekazywanie komponentom widoków odpowiednich propsów identyfikujących zakładki
 *
 */
export class CustomTabs extends React.Component<Props> {
  componentDidMount(): void {
    this.props.initTabs(this.props.location.pathname, this.props.history);
    this.takeKeyOfStaticActiveTab();
  }
  componentDidUpdate(prevProps: Props): void {
    if (
      prevProps.location.pathname !== this.props.location.pathname ||
      prevProps.dynamicTabs.length !== this.props.dynamicTabs.length
    ) {
      this.takeKeyOfStaticActiveTab();
      this.takeKeyOfDynamicActiveTab();
    }
  }

  onTabEdit = (targetKey: string | React.MouseEvent<HTMLElement, MouseEvent>, action: 'add' | 'remove'): void => {
    const parts = this.props.location.pathname.split('/');
    const tabType = parts[parts.length - 2];
    switch (action) {
      case 'remove':
        this.props.closeTab(Number(targetKey), this.props.history, tabType, Number(this.props.activeTab));
        break;
    }
  };
  // metoda "takeKeyOfStaticActiveTab" sprawdza czy adres url jest adresem przypisanym do istniejącej zakładki statycznej
  takeKeyOfStaticActiveTab = (): void => {
    const activeTab /* : StaticTab | undefined */ = staticTabsList.find(
      (tab): boolean => tab.path === this.props.location.pathname,
    );
    if (activeTab) {
      this.props.setActiveTab(`static_${activeTab.key}`);
    }
  };
  // metoda "takeKeyOfDynamicActiveTab" sprawdza czy adres url jest adresem przypisanym do istniejącej zakładki dynamicznej
  takeKeyOfDynamicActiveTab = (): void => {
    const parts = this.props.location.pathname.split('/');
    const pathTabType = parts[parts.length - 2];
    const pathItemId = parts[parts.length - 1];

    const activeTab: any = this.props.dynamicTabs.find(
      (tab: any): boolean => tab.tabType === pathTabType && pathItemId === tab.itemId,
    );
    if (activeTab) {
      const { titleTranslationKey, tabType, itemId } = activeTab;
      const tabAttr = {
        titleTranslationKey,
        tabType,
        itemId,
      };
      const prevActive = this.props.dynamicTabs[Number(this.props.activeTab)];
      if (!prevActive || prevActive.tabType !== pathTabType || prevActive.itemId !== itemId) {
        this.props.openNewTab(tabAttr, this.props.history, true);
      }
    }
  };
  renderDynamicTabs = (): Array<React.ReactElement> =>
    this.props.dynamicTabs
      .filter(tab => this.props.checkAccess(tab.tabType))
      .map((tab, index: number) => {
        const Component = dynamicTabContent[tab.tabType];
        return (
          <TabPane
            tab={
              <NavLink className='closable' activeClassName='active-link' to={`/${tab.tabType}/${tab.itemId}`}>
                {tab.leftPanel.loading ? (
                  <span>
                    <span className='limit small'>{this.props.t(tab.titleTranslationKey)}</span>
                    <Loader style={{ fontSize: '24px' }} />
                  </span>
                ) : (
                  <span className='limit large'>{this.props.t(tab.titleTranslationKey)}</span>
                )}
              </NavLink>
            }
            key={`${index}`}
            closable={true}
          >
            <Route exact path={`/${tab.tabType}/${tab.itemId}`} render={props => <Component {...props} id={tab.id} />} />
          </TabPane>
        );
      });

  render(): React.ReactElement {
    return (
      <Tabs
        type='editable-card'
        hideAdd={true}
        onEdit={this.onTabEdit}
        onChange={this.props.setActiveTab}
        activeKey={this.props.activeTab}
      >
        {staticTabsList
          .filter(tab => this.props.checkAccess(tab.accessName))
          .map((tab, index) => {
            const Component = tab.component;
            return (
              <TabPane
                tab={
                  <NavLink activeClassName='active-link' to={`${tab.path}`}>
                    <span className='limit'>{this.props.t(tab.titleTranslationKey)}</span>
                  </NavLink>
                }
                key={`static_${index}`}
                closable={false}
                data-cy={tab.accessName}
              >
                <Route path={`${tab.path}`} render={props => <Component view={tab.view} {...props} />} />
              </TabPane>
            );
          })}
        {this.renderDynamicTabs()}
      </Tabs>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  dynamicTabs: state.DynamicTabs.tabList,
  activeTab: state.DynamicTabs.activeTab,
});

const mapDispatchToProps = (dispatch: any) => ({
  initTabs: (path: string, history: any) => dispatch(initTabs(path, history)),
  closeTab: (index: number, history: any, tabType: string, activeTabId: number) =>
    dispatch(closeTab(index, history, tabType, activeTabId)),
  openNewTab: (data: DynamicTabAttributes, history: any, noHistoryPush: boolean, tabsAmountOnInit?: number) =>
    dispatch(openNewTab(data, history, noHistoryPush, tabsAmountOnInit)),
  setActiveTab: (activateKey: string) => dispatch(setActiveTab(activateKey)),
  closeAllTabs: () => dispatch(closeAllTabs()),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withAccessProvider,
  withTranslation(),
)(CustomTabs) as React.ComponentType;
